import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { API } from "@/lib/api";
import { SECTIONS, sectionLabel, UI, SPECTRALOOM_URL } from "@/lib/constants";
import { HeroBanner } from "@/components/HeroBanner";
import { ContentCard } from "@/components/ContentCard";
import { Footer } from "@/components/Footer";

export default function BlogHome() {
  const [lang, setLang] = useState(sessionStorage.getItem("sl_lang") || "IT");
  const [section, setSection] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = UI[lang];

  useEffect(() => {
    sessionStorage.setItem("sl_lang", lang);
    setLoading(true);
    const params = {};
    if (section !== "all") params.category = section;
    axios
      .get(`${API}/content`, { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [lang, section]);

  return (
    <div className="min-h-screen bg-[#0B0D12]">
      <title>Spectraloom Blog — {t.heroHeadline}</title>
      <meta name="description" content={t.heroSub} />
      <meta property="og:title" content="Spectraloom Blog" />
      <meta property="og:site_name" content="Spectraloom Blog" />
      <meta property="og:type" content="website" />

      <header
        data-testid="site-header"
        className="sticky top-0 z-40 border-b border-[#2D3748] bg-[#0B0D12]/85 backdrop-blur-xl"
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" data-testid="logo-home" className="flex items-center gap-3">
            <img src="/spectraloom-logo.jpg" alt="Spectraloom Blog" className="h-10 w-10 rounded-lg object-contain bg-[#0B0D12]" />
            <span className="font-heading text-lg font-bold tracking-tight text-white">Spectraloom Blog</span>
          </a>
          <div className="flex items-center gap-3">
            <a
              href={SPECTRALOOM_URL}
              data-testid="back-to-app"
              className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-3.5 py-1.5 text-xs font-semibold text-[#a78bfa] transition-colors hover:bg-[#8B5CF6]/20 hover:text-white"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {t.backToApp}
            </a>
            <div data-testid="language-toggle" className="flex items-center rounded-full border border-[#2D3748] p-1 bg-[#121620]">
              {["EN", "IT"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold font-mono transition-colors ${
                    lang === l ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        <HeroBanner lang={lang} />

        <nav data-testid="section-filter" className="flex flex-wrap gap-2.5 mb-10">
          <FilterButton active={section === "all"} onClick={() => setSection("all")} label={t.all} testid="all" />
          {SECTIONS.map((s) => (
            <FilterButton
              key={s.slug}
              active={section === s.slug}
              onClick={() => setSection(s.slug)}
              label={sectionLabel(s.slug, lang)}
              testid={s.slug}
            />
          ))}
        </nav>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-[#121620] border border-[#2D3748] animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-24 text-[#64748B]">
            {t.noContent}
          </div>
        ) : (
          <div data-testid="content-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {items.map((item) => (
              <ContentCard key={item.id} item={item} lang={lang} />
            ))}
          </div>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  );
}

const FilterButton = ({ active, onClick, label, testid }) => (
  <button
    data-testid={`filter-${testid}`}
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
      active
        ? "bg-white text-[#0B0D12] border-white"
        : "bg-[#121620] text-[#94A3B8] border-[#2D3748] hover:border-[#8B5CF6]/50 hover:text-white"
    }`}
  >
    {label}
  </button>
);
